import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IoArrowBack, IoCheckmarkCircle, IoAddOutline, IoTrashOutline, IoCloudUploadOutline, IoPlay, IoDownloadOutline, IoStar, IoStarOutline } from "react-icons/io5";
import { toast } from "sonner";
import { THEMES, FEATURED_THEME_KEYS } from "@/lib/themes";
import { INTROS } from "@/lib/intro";
import BootIntro from "@/components/BootIntro";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import ImageUpload from "@/components/ImageUpload";
import { applyFavicon } from "@/lib/branding";
import { DEFAULT_WELCOME_MESSAGES } from "@/lib/social";
import {
  listAmbiences, createAmbience, updateAmbience, uploadAmbienceAudio, deleteAmbience, ambienceUrl,
  listCategories, createCategory, updateCategory, deleteCategory,
  listSubscribers, deleteSubscriber, getSettings, updateSettings,
  uploadIntroVideo, deleteIntroVideo,
  listAnnouncements, createAnnouncement, deleteAnnouncement, listCreations, emailStatus,
  getPageConfig, updatePageConfig, openBillingPortal, adminStats, deleteMyAccount,
} from "@/lib/api";

const fs = { background: "var(--bg-primary)", color: "var(--text-primary)", borderColor: "var(--border-c)", borderRadius: "var(--radius-base)" };
const DISCORD_INVITE = "https://discord.gg/sFCKWpgRg";

const AMBIENCE_ICONS = ["🎵","🌙","🌧️","🔥","🌊","🍃","🎐","🏯","⛩️","🌸","❄️","☕","📖","🕯️","🌌","🎹","🐉","🦊","💮","🎏","🌿","⚡","🫧","🪷"];

function BrandingManager() {
  const { data, refetch } = useQuery({ queryKey: ["branding"], queryFn: () => getPageConfig("branding") });
  const [icon, setIcon] = useState(null);
  const cur = icon !== null ? icon : (data?.app_icon || "");
  const save = async () => {
    try {
      await updatePageConfig("branding", { app_icon: cur });
      applyFavicon(cur);
      window.dispatchEvent(new Event("branding-changed"));
      toast.success("Icône de l'application mise à jour");
      setIcon(null); refetch();
    } catch { toast.error("Échec de l'enregistrement"); }
  };
  return (
    <div className="space-y-4" data-testid="branding-manager">
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Cette icône s'affiche dans l'onglet du navigateur et quand on ajoute le site à l'écran d'accueil. Idéalement une image carrée (512×512, PNG).
      </p>
      <div className="flex items-center gap-4">
        <img src={cur || "/icon-192.png"} alt="Aperçu de l'icône" data-testid="branding-preview"
          style={{ width: 72, height: 72, borderRadius: 16, objectFit: "cover", border: "1px solid var(--border-c)", background: "var(--bg-secondary)" }} />
        <div className="flex-1"><ImageUpload label="Icône de l'application" testid="branding-icon" value={cur} onChange={(v) => setIcon(v || "")} /></div>
      </div>
      <div className="flex gap-3">
        {cur && <button data-testid="branding-reset" onClick={() => setIcon("")} className="p-3 text-sm font-semibold" style={{ borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", cursor: "pointer" }}>Réinitialiser</button>}
        <button data-testid="branding-save" onClick={save} className="flex-1 p-3 text-sm font-bold" style={{ borderRadius: 12, border: "none", background: "var(--brand)", color: "#fff", cursor: "pointer" }}>Enregistrer l'icône</button>
      </div>
    </div>
  );
}

function AmbienceManager() {
  const { isAdmin, ambienceId, selectAmbience, sfx } = useApp();
  const { data: list = [], refetch } = useQuery({ queryKey: ["ambiences"], queryFn: listAmbiences });
  const { data: settings, refetch: refetchSettings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [title, setTitle] = useState(""); const [emoji, setEmoji] = useState("🎵");
  const defaultId = settings?.default_ambience_id || "";

  const setDefault = async (id) => {
    await updateSettings({ ...settings, default_ambience_id: id });
    toast.success(id ? "Ambiance par défaut définie" : "Aucune ambiance par défaut");
    refetchSettings();
  };

  const add = async () => {
    if (!title.trim()) return toast.error("Titre requis");
    const a = await createAmbience({ title: title.trim(), emoji: emoji || "🎵", order: list.length });
    toast.success("Ambiance créée — ajoute un fichier audio"); setTitle(""); setEmoji("🎵"); refetch();
    return a;
  };
  const upload = async (id, file) => { if (!file) return; try { await uploadAmbienceAudio(id, file); toast.success("Audio ajouté"); refetch(); } catch { toast.error("Fichier trop lourd (max 12 Mo) ?"); } };
  const del = async (id) => { await deleteAmbience(id); if (ambienceId === id) selectAmbience(""); refetch(); };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-testid="ambience-list">
        <button onClick={() => { sfx("nav"); selectAmbience(""); }} data-testid="ambience-none"
          className="p-4 border text-left" style={{ ...fs, background: "var(--bg-secondary)", outline: !ambienceId ? "2px solid var(--brand)" : "none" }}>
          <div className="text-2xl mb-1">🔇</div><p className="text-sm" style={{ color: "var(--text-primary)" }}>Aucune</p>
        </button>
        {list.map((a) => (
          <div key={a.id} data-testid={`ambience-${a.id}`} className="p-4 border relative" style={{ ...fs, background: "var(--bg-secondary)", outline: ambienceId === a.id ? "2px solid var(--brand)" : "none" }}>
            {defaultId === a.id && (
              <span className="absolute top-2 right-2 flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "var(--brand)", color: "var(--bg-primary)" }} data-testid={`ambience-default-badge-${a.id}`}><IoStar size={10} /> Défaut</span>
            )}
            <button onClick={() => { sfx("select"); selectAmbience(a.id); }} className="text-left w-full">
              <div className="text-2xl mb-1">{a.emoji}</div>
              <p className="text-sm truncate" style={{ color: "var(--text-primary)" }}>{a.title}</p>
            </button>
            <div className="flex items-center gap-3 mt-2">
              <a href={ambienceUrl(a.id)} target="_blank" rel="noreferrer" className="text-xs" style={{ color: "var(--text-secondary)" }}><IoPlay /></a>
              {isAdmin && (
                <>
                  <button onClick={() => setDefault(defaultId === a.id ? "" : a.id)} className="text-xs" style={{ color: defaultId === a.id ? "var(--brand)" : "var(--text-secondary)" }} title="Définir par défaut" data-testid={`ambience-default-${a.id}`}>
                    {defaultId === a.id ? <IoStar /> : <IoStarOutline />}
                  </button>
                  <label className="text-xs cursor-pointer" style={{ color: "var(--brand)" }} title="Uploader l'audio">
                    <IoCloudUploadOutline /><input type="file" accept="audio/*" hidden onChange={(e) => upload(a.id, e.target.files?.[0])} />
                  </label>
                  <button onClick={() => del(a.id)} className="text-xs" style={{ color: "var(--brand)" }}><IoTrashOutline /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="border p-3" style={{ ...fs, background: "var(--bg-secondary)" }} data-testid="ambience-add">
          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-secondary)" }}>Choisis une icône</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {AMBIENCE_ICONS.map((ic) => (
              <button key={ic} data-testid={`ambience-icon-${ic}`} onClick={() => setEmoji(ic)}
                className="w-9 h-9 flex items-center justify-center text-lg border" style={{ borderColor: "var(--border-c)", borderRadius: "var(--radius-base)", outline: emoji === ic ? "2px solid var(--brand)" : "none", background: "var(--bg-primary)" }}>
                {ic}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="w-11 flex items-center justify-center text-xl border" style={{ borderColor: "var(--border-c)", borderRadius: "var(--radius-base)" }}>{emoji}</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de l'ambiance" className="flex-1 px-3 py-2 border outline-none" style={fs} data-testid="ambience-title-input" />
            <button onClick={add} className="px-4 py-2 font-semibold flex items-center gap-1" style={{ background: "var(--brand)", color: "var(--bg-primary)", borderRadius: "var(--radius-base)" }} data-testid="ambience-add-btn"><IoAddOutline /> Ajouter</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryManager() {
  const { data: list = [], refetch } = useQuery({ queryKey: ["categories"], queryFn: listCategories });
  const [label, setLabel] = useState(""); const [kanji, setKanji] = useState("");

  const add = async () => { if (!label.trim()) return toast.error("Nom requis"); await createCategory({ label: label.trim(), kanji, order: list.length + 1 }); toast.success("Catégorie créée"); setLabel(""); setKanji(""); refetch(); };
  const save = async (c, patch) => { await updateCategory(c.id, { label: patch.label ?? c.label, kanji: patch.kanji ?? c.kanji, order: patch.order ?? c.order }); refetch(); };
  const del = async (c) => { if (!window.confirm(`Supprimer la catégorie « ${c.label} » ? Les créations resteront mais sans catégorie visible.`)) return; await deleteCategory(c.id); refetch(); };
  const move = async (c, dir) => { await save(c, { order: (c.order || 0) + dir }); };

  return (
    <div data-testid="category-manager">
      <div className="space-y-2 mb-4">
        {list.map((c) => (
          <div key={c.id} data-testid={`cat-row-${c.key}`} className="flex items-center gap-2 p-2 border" style={{ ...fs, background: "var(--bg-secondary)" }}>
            <input defaultValue={c.kanji} onBlur={(e) => e.target.value !== c.kanji && save(c, { kanji: e.target.value })} className="w-14 px-2 py-1.5 border outline-none text-center" style={fs} />
            <input defaultValue={c.label} onBlur={(e) => e.target.value !== c.label && save(c, { label: e.target.value })} className="flex-1 px-2 py-1.5 border outline-none" style={fs} />
            <button onClick={() => move(c, -1)} className="px-2" style={{ color: "var(--text-secondary)" }}>↑</button>
            <button onClick={() => move(c, 1)} className="px-2" style={{ color: "var(--text-secondary)" }}>↓</button>
            <button onClick={() => del(c)} className="px-2" style={{ color: "var(--brand)" }}><IoTrashOutline /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={kanji} onChange={(e) => setKanji(e.target.value)} placeholder="漢" className="w-14 px-2 py-2 border outline-none text-center" style={fs} data-testid="cat-new-kanji" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nouvelle catégorie" className="flex-1 px-3 py-2 border outline-none" style={fs} data-testid="cat-new-label" />
        <button onClick={add} className="px-4 py-2 font-semibold flex items-center gap-1" style={{ background: "var(--brand)", color: "var(--bg-primary)", borderRadius: "var(--radius-base)" }} data-testid="cat-add-btn"><IoAddOutline /> Ajouter</button>
      </div>
    </div>
  );
}

function SubscribersManager() {
  const { data: list = [], refetch } = useQuery({ queryKey: ["subscribers"], queryFn: listSubscribers });
  const exportCsv = () => {
    const csv = "email,date\n" + list.map((s) => `${s.email},${s.created_at}`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "abonnes.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="subscribers-manager">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{list.length} abonné(s)</p>
        <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 border text-xs" style={fs}><IoDownloadOutline /> Exporter CSV</button>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {list.map((s) => (
          <div key={s.id} className="flex items-center justify-between p-2 border text-sm" style={{ ...fs, background: "var(--bg-secondary)" }}>
            <span style={{ color: "var(--text-primary)" }}>{s.email}</span>
            <button onClick={async () => { await deleteSubscriber(s.id); refetch(); }} style={{ color: "var(--brand)" }}><IoTrashOutline size={14} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Aucun abonné pour l'instant.</p>}
      </div>
    </div>
  );
}

function IntroManager() {
  const { isAdmin } = useApp();
  const { data: settings, refetch } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [off, setOff] = useState(localStorage.getItem("sx_intro_off") === "1");
  const [preview, setPreview] = useState(null);
  const curType = settings?.intro_type || "tengoku";
  const hasVideo = !!settings?.intro_has_video;
  const hidden = settings?.intro_hidden || [];
  const visibleIntros = INTROS.filter((it) => !hidden.includes(it.key));

  const toggleOff = () => { const nv = !off; setOff(nv); localStorage.setItem("sx_intro_off", nv ? "1" : "0"); };
  const chooseType = async (t) => { await updateSettings({ ...settings, intro_type: t }); toast.success("Intro par défaut mise à jour"); refetch(); };
  const hideIntro = async (key) => {
    const nh = [...new Set([...hidden, key])];
    const patch = { ...settings, intro_hidden: nh };
    if (curType === key) patch.intro_type = INTROS.find((i) => !nh.includes(i.key))?.key || "tengoku";
    await updateSettings(patch); toast.success("Écran de démarrage supprimé"); refetch();
  };
  const restoreAll = async () => { await updateSettings({ ...settings, intro_hidden: [] }); toast.success("Écrans réaffichés"); refetch(); };
  const upload = async (file) => {
    if (!file) return;
    try { await uploadIntroVideo(file); toast.success("Vidéo d'intro importée"); refetch(); }
    catch { toast.error("Vidéo trop lourde (max 10 Mo) ?"); }
  };
  const delVideo = async () => {
    await deleteIntroVideo();
    if (curType === "video") await updateSettings({ ...settings, intro_type: "tengoku", intro_has_video: false });
    toast.success("Vidéo supprimée"); refetch();
  };

  return (
    <div data-testid="intro-manager">
      <label className="flex items-center gap-2 text-sm cursor-pointer mb-4" style={{ color: "var(--text-primary)" }}>
        <input type="checkbox" data-testid="intro-enabled" checked={!off} onChange={toggleOff} /> Afficher l'écran de démarrage (une fois par session)
      </label>

      {/* Random mode */}
      <div data-testid="intro-random" className="p-4 border mb-3 flex items-center justify-between gap-3 flex-wrap"
        style={{ ...fs, background: "var(--bg-secondary)", outline: curType === "random" ? "2px solid var(--brand)" : "none" }}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎲</span>
          <div>
            <span className="font-display text-lg block" style={{ color: "var(--text-primary)" }}>Aléatoire</span>
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>Un écran de démarrage différent à chaque session</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && curType !== "random" && <button data-testid="intro-choose-random" onClick={() => chooseType("random")} className="text-xs px-3 py-1.5 border" style={{ color: "var(--text-primary)", borderColor: "var(--border-c)", borderRadius: "var(--radius-base)" }}>Définir par défaut</button>}
          {curType === "random" && <span className="text-xs flex items-center gap-1" style={{ color: "var(--brand)" }}><IoStar size={11} /> Par défaut</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {visibleIntros.map((it) => (
          <div key={it.key} data-testid={`intro-${it.key}`} className="p-4 border" style={{ ...fs, background: "var(--bg-secondary)", outline: curType === it.key ? "2px solid var(--brand)" : "none" }}>
            <div className="flex items-baseline justify-between">
              <span className="font-display text-lg" style={{ color: "var(--text-primary)" }}>{it.label}</span>
              <span className="font-display" style={{ color: "var(--brand)" }}>{it.kanji}</span>
            </div>
            <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{it.tagline}</p>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <button data-testid={`intro-preview-${it.key}`} onClick={() => setPreview({ type: it.key, hasVideo: false })} className="text-xs" style={{ color: "var(--brand)" }}><IoPlay /> Aperçu</button>
              {isAdmin && curType !== it.key && <button data-testid={`intro-choose-${it.key}`} onClick={() => chooseType(it.key)} className="text-xs" style={{ color: "var(--text-secondary)" }}>Définir</button>}
              {curType === it.key && <span className="text-xs flex items-center gap-1" style={{ color: "var(--brand)" }}><IoStar size={11} /> Par défaut</span>}
              {isAdmin && <button data-testid={`intro-delete-${it.key}`} onClick={() => hideIntro(it.key)} className="text-xs ml-auto" style={{ color: "var(--brand)" }} title="Supprimer cet écran"><IoTrashOutline /></button>}
            </div>
          </div>
        ))}

        {/* video card */}
        <div className="p-4 border" style={{ ...fs, background: "var(--bg-secondary)", outline: curType === "video" ? "2px solid var(--brand)" : "none" }} data-testid="intro-video-card">
          <div className="flex items-baseline justify-between">
            <span className="font-display text-lg" style={{ color: "var(--text-primary)" }}>Ma vidéo</span>
            <span className="font-display" style={{ color: "var(--brand)" }}>🎬</span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{hasVideo ? "Vidéo importée" : "Aucune vidéo (max 10 Mo)"}</p>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {hasVideo && <button onClick={() => setPreview({ type: "video", hasVideo: true })} className="text-xs" style={{ color: "var(--brand)" }} data-testid="intro-preview-video"><IoPlay /> Aperçu</button>}
            {isAdmin && (
              <>
                <label className="text-xs cursor-pointer" style={{ color: "var(--brand)" }} data-testid="intro-video-upload">
                  <IoCloudUploadOutline /> {hasVideo ? "Remplacer" : "Importer"}
                  <input type="file" accept="video/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
                </label>
                {hasVideo && curType !== "video" && <button onClick={() => chooseType("video")} className="text-xs" style={{ color: "var(--text-secondary)" }} data-testid="intro-choose-video">Définir</button>}
                {hasVideo && <button onClick={delVideo} className="text-xs" style={{ color: "var(--brand)" }}><IoTrashOutline /></button>}
              </>
            )}
          </div>
        </div>
      </div>

      {isAdmin && hidden.length > 0 && (
        <button data-testid="intro-restore-all" onClick={restoreAll} className="mt-3 text-xs underline" style={{ color: "var(--text-secondary)" }}>Réafficher les écrans supprimés ({hidden.length})</button>
      )}

      {preview && <BootIntro type={preview.type} hasVideo={preview.hasVideo} onDone={() => setPreview(null)} />}
    </div>
  );
}

function WelcomeMessagesManager() {
  const { data: settings, refetch } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [draft, setDraft] = useState(null);
  const stored = settings?.welcome_messages;
  const list = draft ?? (stored && stored.length ? stored : [...DEFAULT_WELCOME_MESSAGES]);
  const update = (i, v) => setDraft(list.map((m, x) => (x === i ? v : m)));
  const add = () => setDraft([...list, ""]);
  const remove = (i) => setDraft(list.filter((_, x) => x !== i));
  const restore = () => setDraft([...DEFAULT_WELCOME_MESSAGES]);
  const save = async () => {
    const clean = list.map((m) => m.trim()).filter(Boolean);
    if (!clean.length) return toast.error("Ajoute au moins un message");
    await updateSettings({ ...settings, welcome_messages: clean });
    toast.success("Messages de bienvenue enregistrés"); refetch(); setDraft(null);
  };
  return (
    <div data-testid="welcome-messages-manager">
      <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>Quand un membre connecté entre dans le Dōjō, un de ces messages s'affiche au hasard avec sa photo de profil (à la place du logo). Utilise <span style={{ color: "var(--brand)" }}>{"{name}"}</span> pour insérer son prénom.</p>
      <div className="space-y-2 mb-3">
        {list.map((m, i) => (
          <div key={i} className="flex items-center gap-2">
            <input data-testid={`welcome-msg-${i}`} value={m} onChange={(e) => update(i, e.target.value)} placeholder={`Message ${i + 1} (ex: Bon retour, {name} !)`}
              className="flex-1 px-3 py-2 border outline-none" style={fs} />
            <button data-testid={`welcome-del-${i}`} onClick={() => remove(i)} title="Supprimer"
              className="p-2 flex items-center justify-center" style={{ borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "#e11d2a", cursor: "pointer" }}><IoTrashOutline size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={add} data-testid="welcome-add" className="px-4 py-2 font-semibold flex items-center gap-1.5" style={{ border: "1px dashed var(--border-c)", background: "transparent", color: "var(--text-primary)", borderRadius: "var(--radius-base)", cursor: "pointer" }}><IoAddOutline size={16} /> Ajouter un message</button>
        <button onClick={restore} data-testid="welcome-restore" className="px-4 py-2 font-semibold" style={{ border: "1px solid var(--border-c)", background: "transparent", color: "var(--text-secondary)", borderRadius: "var(--radius-base)", cursor: "pointer" }}>Restaurer les 10 par défaut</button>
        <button onClick={save} data-testid="welcome-save" className="px-5 py-2 font-semibold" style={{ background: "var(--brand)", color: "var(--bg-primary)", borderRadius: "var(--radius-base)", cursor: "pointer" }}>Enregistrer</button>
      </div>
    </div>
  );
}

function AnnouncementsManager() {
  const { data: list = [], refetch } = useQuery({ queryKey: ["announcements"], queryFn: listAnnouncements });
  const { data: creations = [] } = useQuery({ queryKey: ["creations"], queryFn: () => listCreations() });
  const { data: mail } = useQuery({ queryKey: ["emailStatus"], queryFn: emailStatus });
  const [title, setTitle] = useState(""); const [text, setText] = useState(""); const [cid, setCid] = useState(""); const [notify, setNotify] = useState(false);
  const add = async () => {
    if (!title.trim() || !text.trim()) return toast.error("Titre et message requis");
    await createAnnouncement({ title: title.trim(), text: text.trim(), creation_id: cid, notify_email: notify });
    toast.success(notify ? "Annonce publiée + emails envoyés aux abonnés" : "Annonce publiée — visible par tous les visiteurs");
    setTitle(""); setText(""); setCid(""); setNotify(false); refetch();
  };
  const del = async (id) => { await deleteAnnouncement(id); refetch(); };
  return (
    <div data-testid="announcements-manager">
      <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>Publie une annonce (ex: nouvelle création). Chaque visiteur la verra une fois à sa prochaine visite.</p>
      <div className="space-y-2 mb-3">
        {list.map((a) => (
          <div key={a.id} data-testid={`announcement-row-${a.id}`} className="flex items-start justify-between gap-2 p-3 border" style={{ ...fs, background: "var(--bg-secondary)" }}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{a.title}</p>
              <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{a.text}</p>
            </div>
            <button onClick={() => del(a.id)} style={{ color: "var(--brand)" }} data-testid={`announcement-del-${a.id}`}><IoTrashOutline size={16} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Aucune annonce active.</p>}
      </div>
      <div className="border p-3 space-y-2" style={{ ...fs, background: "var(--bg-secondary)" }}>
        <input data-testid="announcement-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de la nouveauté (ex: Épisode 3 : C'est lui le commandant)" className="w-full px-3 py-2 border outline-none" style={fs} />
        <textarea data-testid="announcement-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Message…" className="w-full px-3 py-2 border outline-none resize-none" style={fs} />
        <select data-testid="announcement-creation" value={cid} onChange={(e) => setCid(e.target.value)} className="w-full px-3 py-2 border outline-none" style={fs}>
          <option value="">Lier à une création (optionnel)</option>
          {creations.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--text-primary)" }} data-testid="announcement-notify-label">
          <input type="checkbox" data-testid="announcement-notify" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ accentColor: "var(--brand)" }} />
          Envoyer aussi par email aux abonnés
        </label>
        {mail && !mail.configured && (
          <p className="text-[11px]" style={{ color: "var(--brand)" }}>⚠ Email non configuré côté serveur — l'envoi sera ignoré.</p>
        )}
        <button onClick={add} data-testid="announcement-add" className="px-4 py-2 font-semibold flex items-center gap-1" style={{ background: "var(--brand)", color: "var(--bg-primary)", borderRadius: "var(--radius-base)" }}><IoAddOutline /> Publier</button>
      </div>
    </div>
  );
}

const POSTER_PREVIEW_PX = { compact: 40, normal: 58, grand: 78 };
function MiniPoster({ size }) {
  const w = POSTER_PREVIEW_PX[size] || POSTER_PREVIEW_PX.normal;
  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0" data-testid="poster-preview">
      <div style={{ width: w, height: w * 1.5, borderRadius: "var(--radius-base)", background: "linear-gradient(155deg, var(--brand) 0%, var(--bg-secondary) 70%)", border: "1px solid var(--border-c)", position: "relative", overflow: "hidden", transition: "width .22s ease, height .22s ease", boxShadow: "0 4px 14px rgba(0,0,0,0.4)" }}>
        <div style={{ position: "absolute", left: 5, right: 14, bottom: 10, height: 3, background: "rgba(255,255,255,0.85)", borderRadius: 2 }} />
        <div style={{ position: "absolute", left: 5, right: 22, bottom: 5, height: 2, background: "rgba(255,255,255,0.5)", borderRadius: 2 }} />
      </div>
      <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>Aperçu</span>
    </div>
  );
}

const NERODUS_CTX = [
  { k: "hub", label: "Hub (accueil)" },
  { k: "ecole", label: "École (liste des formations)" },
  { k: "formation", label: "Formation (chemin des portes)" },
  { k: "lecon", label: "Leçon / Porte" },
  { k: "biblio", label: "Bibliothèque" },
  { k: "membres", label: "Membres" },
  { k: "communaute", label: "Communauté" },
  { k: "accompagnement", label: "Accompagnement" },
  { k: "carnet", label: "Mon Carnet (le Drive)" },
];

function NerodusManager() {
  const { data, refetch } = useQuery({ queryKey: ["nerodusCfg"], queryFn: () => getPageConfig("nerodus") });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = draft || data;

  const set = (k, v) => setDraft({ ...cfg, [k]: v });
  const setCtx = (ck, field, v) => setDraft({ ...cfg, contexts: { ...cfg.contexts, [ck]: { ...(cfg.contexts?.[ck] || {}), [field]: v } } });
  const save = async () => {
    setBusy(true);
    try { await updatePageConfig("nerodus", cfg); toast.success("Nerodus mis à jour"); setDraft(null); refetch(); }
    catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  if (!cfg) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;

  const onImg = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop lourde (max 2 Mo)"); return; }
    const r = new FileReader();
    r.onload = () => set("image", r.result);
    r.readAsDataURL(file);
  };

  return (
    <div data-testid="nerodus-manager">
      <div className="flex items-center gap-3 mb-4 p-3" style={{ borderRadius: 14, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)" }}>
        <img src={cfg.image || "/nerodus.png"} alt="Nerodus" style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "2px solid var(--brand)", flexShrink: 0 }} />
        <div className="flex-1">
          <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>Personnalise ce que dit Nerodus sur chaque page. Un conseil par ligne.</p>
          <div className="flex gap-2 flex-wrap">
            <label data-testid="nerodus-image-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 cursor-pointer text-xs font-bold" style={{ borderRadius: 999, border: "1px solid var(--brand)", color: "var(--brand)", background: "var(--bg-secondary)" }}>
              <IoCloudUploadOutline size={14} /> Changer l'image
              <input type="file" accept="image/*" hidden onChange={(e) => onImg(e.target.files?.[0])} />
            </label>
            {cfg.image && (
              <button data-testid="nerodus-image-reset" onClick={() => set("image", "")} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold" style={{ borderRadius: 999, border: "1px solid var(--border-c)", color: "var(--text-secondary)", background: "var(--bg-secondary)" }}>
                Image par défaut
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest mb-1.5" style={{ color: "var(--text-secondary)" }}>Nom</p>
          <input data-testid="nerodus-name" value={cfg.name || ""} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2 border outline-none" style={fs} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest mb-1.5" style={{ color: "var(--text-secondary)" }}>Rôle</p>
          <input data-testid="nerodus-role" value={cfg.role || ""} onChange={(e) => set("role", e.target.value)} className="w-full px-3 py-2 border outline-none" style={fs} />
        </div>
      </div>
      <div className="space-y-3">
        {NERODUS_CTX.map(({ k, label }) => {
          const c = cfg.contexts?.[k] || {};
          return (
            <details key={k} data-testid={`nerodus-ctx-${k}`} className="border" style={{ borderColor: "var(--border-c)", borderRadius: 12, background: "var(--bg-secondary)" }}>
              <summary className="px-3 py-2.5 cursor-pointer text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{label}</summary>
              <div className="px-3 pb-3 space-y-2">
                <input placeholder="Phrase d'accroche" value={c.greeting || ""} onChange={(e) => setCtx(k, "greeting", e.target.value)} className="w-full px-3 py-2 border outline-none text-sm" style={fs} data-testid={`nerodus-greeting-${k}`} />
                <textarea placeholder="Un conseil par ligne" rows={3} value={(c.lines || []).join("\n")} onChange={(e) => setCtx(k, "lines", e.target.value.split("\n"))} className="w-full px-3 py-2 border outline-none text-sm resize-y" style={fs} data-testid={`nerodus-lines-${k}`} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input placeholder="Libellé du bouton (optionnel)" value={c.cta_label || ""} onChange={(e) => setCtx(k, "cta_label", e.target.value)} className="w-full px-3 py-2 border outline-none text-sm" style={fs} />
                  <input placeholder="Lien du bouton (ex: /accompagnement)" value={c.cta_to || ""} onChange={(e) => setCtx(k, "cta_to", e.target.value)} className="w-full px-3 py-2 border outline-none text-sm" style={fs} />
                </div>
              </div>
            </details>
          );
        })}
      </div>
      <button onClick={save} disabled={busy} data-testid="nerodus-save" className="mt-4 px-5 py-2.5 font-bold" style={{ background: "var(--brand)", color: "#fff", borderRadius: 12, opacity: busy ? 0.6 : 1 }}>{busy ? "Enregistrement…" : "Enregistrer Nerodus"}</button>
    </div>
  );
}

const THEME_PREVIEWS = {
  dojo: { bg: "#ffffff", card: "#f5f5f5", brand: "#e11d2a", text: "#111111" },
  tengoku: { bg: "#0a0a0b", card: "#131316", brand: "#e60012", text: "#f8fafc" },
  horizon: { bg: "#04191A", card: "#082A2A", brand: "#1FA39C", text: "#EAF4F1", accent: "#e08d8d" },
  yoru: { bg: "#14110e", card: "#272019", brand: "#c9a24a", text: "#f3ece0" },
  kernel: { bg: "#05080a", card: "#0f1619", brand: "#39ff14", text: "#7dfca3" },
  studio: { bg: "#ffffff", card: "#f3f3f1", brand: "#0a0a0a", text: "#0a0a0a" },
  brume: { bg: "#1b2735", card: "#2f465e", brand: "#6ea8fe", text: "#eef2f7" },
  neon: { bg: "#0d0a1f", card: "#1e1746", brand: "#ff2fd0", text: "#f0e9ff" },
  muji: { bg: "#faf9f6", card: "#f1efe9", brand: "#9a8f7d", text: "#2b2b2b" },
  mugen: { bg: "#F3EAD3", card: "#FBF4E2", brand: "#8E201C", text: "#3A2A17", accent: "#A97B30" },
  sylve: { bg: "#0D1A10", card: "#1E3523", brand: "#5FB36A", text: "#E7F0E2" },
  tora: { bg: "#0B0A08", card: "#211A11", brand: "#FF6A00", text: "#F7F2E9" },
  ukiyo: { bg: "#ECE4D3", card: "#F6F0E2", brand: "#1C4A6E", text: "#21303A", accent: "#CF4A2E" },
  raijin: { bg: "#0C0B07", card: "#211E10", brand: "#FFD60A", text: "#F7F6E8", accent: "#FFB300" },
};

function VolumeSlider({ value, onChange }) {
  const pct = Math.round(value * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Volume de l'ambiance</span>
        <span className="text-sm font-bold tabular-nums" style={{ color: "var(--brand)" }}>{pct}%</span>
      </div>
      <input type="range" min="0" max="1" step="0.01" value={value} data-testid="volume-slider"
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full td-range" style={{ accentColor: "var(--brand)" }} />
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testid}
      className="w-full flex items-center justify-between gap-4 p-4 text-left transition-all"
      style={{ borderRadius: "calc(var(--radius-base) + 4px)", border: "1px solid var(--border-c)", background: "var(--bg-secondary)" }}>
      <span>
        <span className="text-sm font-semibold block" style={{ color: "var(--text-primary)" }}>{label}</span>
        {hint && <span className="text-xs block mt-0.5" style={{ color: "var(--text-secondary)" }}>{hint}</span>}
      </span>
      <span className="relative flex-shrink-0" style={{ width: 46, height: 26, borderRadius: 999, background: checked ? "var(--brand)" : "var(--bg-tertiary)", border: "1px solid var(--border-c)", transition: "background .2s ease" }}>
        <span style={{ position: "absolute", top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .2s ease", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
      </span>
    </button>
  );
}

const PremiumCard = ({ title, sub, kanji, children, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden mb-6"
    style={{ borderRadius: 22, border: "1px solid var(--border-c)", background: "var(--surface-card, var(--bg-secondary))", boxShadow: "var(--surface-shadow)" }}>
    {kanji && <span aria-hidden style={{ position: "absolute", right: -8, top: -18, fontSize: 96, fontWeight: 800, color: "var(--text-primary)", opacity: 0.05, pointerEvents: "none", lineHeight: 1, fontFamily: "var(--font-display)" }}>{kanji}</span>}
    <div className="relative p-5 md:p-7">
      {title && <h3 className="font-display text-xl md:text-2xl mb-1" style={{ color: "var(--text-primary)" }}>{title}</h3>}
      {sub && <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{sub}</p>}
      {children}
    </div>
  </div>
);

function QuestionnaireManager() {
  const nav = useNavigate();
  const { data: cfg, refetch } = useQuery({ queryKey: ["pc", "questionnaire"], queryFn: () => getPageConfig("questionnaire") });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = draft || cfg;
  if (!d) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;
  const set = (k, v) => setDraft({ ...d, [k]: v });
  const setQ = (i, v) => setDraft({ ...d, questions: d.questions.map((q, x) => (x === i ? { ...q, text: v } : q)) });
  const addQ = () => setDraft({ ...d, questions: [...(d.questions || []), { id: `q${Date.now()}`, text: "" }] });
  const delQ = (i) => setDraft({ ...d, questions: d.questions.filter((_, x) => x !== i) });
  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...d, questions: (d.questions || []).filter((q) => (q.text || "").trim()) };
      await updatePageConfig("questionnaire", clean); toast.success("Questionnaire mis à jour"); setDraft(null); refetch();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const iSt = { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-primary)", boxSizing: "border-box", fontFamily: "inherit" };
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Accueil de Nerodus (titre)</label>
        <input style={iSt} value={d.intro_greeting || ""} onChange={(e) => set("intro_greeting", e.target.value)} data-testid="q-intro-greeting" />
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Message d'accueil</label>
        <textarea style={{ ...iSt, minHeight: 70, resize: "vertical" }} value={d.intro_text || ""} onChange={(e) => set("intro_text", e.target.value)} data-testid="q-intro-text" />
      </div>
      <div className="border-t pt-4" style={{ borderColor: "var(--border-c)" }}>
        <p className="text-sm font-bold mb-3" style={{ color: "var(--text-primary)" }}>Les questions posées à l'arrivée</p>
        <div className="space-y-2">
          {(d.questions || []).map((q, i) => (
            <div key={q.id} className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: "var(--brand)", width: 22 }}>{i + 1}.</span>
              <input style={iSt} value={q.text} onChange={(e) => setQ(i, e.target.value)} data-testid={`q-question-${i}`} placeholder="Ta question…" />
              <button onClick={() => delQ(i)} data-testid={`q-del-${i}`} style={{ color: "var(--brand)", padding: 6 }}><IoTrashOutline size={17} /></button>
            </div>
          ))}
        </div>
        <button onClick={addQ} data-testid="q-add" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "var(--brand)" }}><IoAddOutline size={16} /> Ajouter une question</button>
      </div>
      <button onClick={save} disabled={busy} data-testid="q-save" className="px-5 py-2.5 font-bold" style={{ background: "var(--brand)", color: "#fff", borderRadius: 12, opacity: busy ? 0.6 : 1 }}>{busy ? "Enregistrement…" : "Enregistrer le questionnaire"}</button>
      <button onClick={() => nav("/profil?apercu=1")} data-testid="q-preview" className="ml-3 px-5 py-2.5 font-bold" style={{ background: "transparent", color: "var(--brand)", border: "1.5px solid var(--brand)", borderRadius: 12 }}>👁️ Tester l'onboarding</button>
    </div>
  );
}

function RenzokuOfferManager() {
  const { data: cfg, refetch } = useQuery({ queryKey: ["pc", "accompagnement"], queryFn: () => getPageConfig("accompagnement") });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = draft || cfg;
  if (!d) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;
  const set = (k, v) => setDraft({ ...d, [k]: v });
  const freeOn = d.renzoku_free_on_subscribe !== false;
  const save = async () => {
    setBusy(true);
    try {
      await updatePageConfig("accompagnement", {
        renzoku_free_on_subscribe: freeOn,
        renzoku_free_count: Math.max(0, parseInt(d.renzoku_free_count, 10) || 0),
        renzoku_calendly_url: (d.renzoku_calendly_url || "").trim(),
      });
      toast.success("Offre Renzoku mise à jour"); setDraft(null); refetch();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const iSt = { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-primary)", boxSizing: "border-box", fontFamily: "inherit" };
  return (
    <div className="space-y-4">
      <ToggleRow testid="renzoku-free-toggle" label="Offrir un Renzoku à chaque nouvel abonné"
        hint="Les nouveaux abonnés payants (Stripe) reçoivent une séance offerte à réserver." checked={freeOn}
        onChange={(v) => set("renzoku_free_on_subscribe", v)} />
      {freeOn && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Nombre de séances offertes</label>
          <input type="number" min="0" step="1" style={{ ...iSt, maxWidth: 120 }} value={d.renzoku_free_count ?? 1} onChange={(e) => set("renzoku_free_count", e.target.value)} data-testid="renzoku-free-count" />
        </div>
      )}
      <div>
        <label className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Lien Calendly (réservation Renzoku)</label>
        <input style={iSt} placeholder="https://calendly.com/ton-lien" value={d.renzoku_calendly_url || ""} onChange={(e) => set("renzoku_calendly_url", e.target.value)} data-testid="renzoku-calendly" />
        <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>Utilisé pour le bouton « Réserver mon créneau » quand un membre a un crédit Renzoku.</p>
      </div>
      <p className="text-[13px]" style={{ color: "var(--text-secondary)" }}>💡 Pour offrir manuellement des séances à un membre précis, utilise la <b>console Membres</b> (bouton + dans la colonne Renzoku).</p>
      <button onClick={save} disabled={busy} data-testid="renzoku-save" className="px-5 py-2.5 font-bold" style={{ background: "var(--brand)", color: "#fff", borderRadius: 12, opacity: busy ? 0.6 : 1 }}>{busy ? "Enregistrement…" : "Enregistrer l'offre"}</button>
    </div>
  );
}

function TourManager() {
  const { data: cfg, refetch } = useQuery({ queryKey: ["pc", "tour"], queryFn: () => getPageConfig("tour") });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = draft || cfg;
  if (!d) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;
  const ROUTES = ["/hub", "/ecole", "/bibliotheque", "/carnet", "/communaute", "/membres", "/accompagnement", "/parametres", "/nouveautes", "/tutoriel"];
  const set = (k, v) => setDraft({ ...d, [k]: v });
  const setStep = (i, field, v) => setDraft({ ...d, steps: d.steps.map((st, x) => (x === i ? { ...st, [field]: v } : st)) });
  const addStep = () => setDraft({ ...d, steps: [...(d.steps || []), { id: `s${Date.now()}`, route: "/hub", kanji: "天", title: "", text: "" }] });
  const delStep = (i) => setDraft({ ...d, steps: d.steps.filter((_, x) => x !== i) });
  const move = (i, dir) => {
    const arr = [...d.steps]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setDraft({ ...d, steps: arr });
  };
  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...d, steps: (d.steps || []).filter((st) => (st.title || "").trim() || (st.text || "").trim()) };
      await updatePageConfig("tour", clean); toast.success("Visite guidée mise à jour"); setDraft(null); refetch();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const iSt = { width: "100%", padding: "9px 11px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 13.5, color: "var(--text-primary)", background: "var(--bg-primary)", boxSizing: "border-box", fontFamily: "inherit" };
  const lbl = { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text-secondary)" };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label style={lbl}>Proposition — titre</label><input style={iSt} value={d.intro_title || ""} onChange={(e) => set("intro_title", e.target.value)} data-testid="tour-intro-title" /></div>
        <div><label style={lbl}>Message de fin</label><input style={iSt} value={d.outro_title || ""} onChange={(e) => set("outro_title", e.target.value)} data-testid="tour-outro-title" /></div>
      </div>
      <div><label style={lbl}>Proposition — texte</label><textarea style={{ ...iSt, minHeight: 60, resize: "vertical" }} value={d.intro_text || ""} onChange={(e) => set("intro_text", e.target.value)} data-testid="tour-intro-text" /></div>
      <div><label style={lbl}>Fin — texte (retour à l'accueil)</label><textarea style={{ ...iSt, minHeight: 60, resize: "vertical" }} value={d.outro_text || ""} onChange={(e) => set("outro_text", e.target.value)} data-testid="tour-outro-text" /></div>

      <div className="border-t pt-4" style={{ borderColor: "var(--border-c)" }}>
        <p className="text-sm font-bold mb-3" style={{ color: "var(--text-primary)" }}>Les étapes de la visite</p>
        <div className="space-y-4">
          {(d.steps || []).map((st, i) => (
            <div key={st.id} className="p-3" style={{ borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-primary)" }} data-testid={`tour-step-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold" style={{ color: "var(--brand)" }}>{i + 1}.</span>
                <select style={{ ...iSt, maxWidth: 160 }} value={st.route} onChange={(e) => setStep(i, "route", e.target.value)} data-testid={`tour-step-route-${i}`}>
                  {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input style={{ ...iSt, maxWidth: 60, textAlign: "center" }} value={st.kanji || ""} onChange={(e) => setStep(i, "kanji", e.target.value)} data-testid={`tour-step-kanji-${i}`} placeholder="漢" />
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => move(i, -1)} style={{ padding: 4, color: "var(--text-secondary)" }} title="Monter">↑</button>
                  <button onClick={() => move(i, 1)} style={{ padding: 4, color: "var(--text-secondary)" }} title="Descendre">↓</button>
                  <button onClick={() => delStep(i)} style={{ padding: 4, color: "var(--brand)" }} data-testid={`tour-step-del-${i}`}><IoTrashOutline size={16} /></button>
                </div>
              </div>
              <input style={{ ...iSt, marginBottom: 8 }} value={st.title || ""} onChange={(e) => setStep(i, "title", e.target.value)} placeholder="Titre de l'étape" data-testid={`tour-step-title-${i}`} />
              <textarea style={{ ...iSt, minHeight: 56, resize: "vertical" }} value={st.text || ""} onChange={(e) => setStep(i, "text", e.target.value)} placeholder="Ce que dit Nerodus…" data-testid={`tour-step-text-${i}`} />
            </div>
          ))}
        </div>
        <button onClick={addStep} data-testid="tour-add-step" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "var(--brand)" }}><IoAddOutline size={16} /> Ajouter une étape</button>
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={save} disabled={busy} data-testid="tour-save" className="px-5 py-2.5 font-bold" style={{ background: "var(--brand)", color: "#fff", borderRadius: 12, opacity: busy ? 0.6 : 1 }}>{busy ? "Enregistrement…" : "Enregistrer la visite"}</button>
        <button onClick={() => { try { sessionStorage.setItem("sx_intro_shown", "1"); } catch (e) {} window.dispatchEvent(new Event("td-start-tour")); }} data-testid="tour-preview" className="px-5 py-2.5 font-bold" style={{ background: "transparent", color: "var(--brand)", border: "1.5px solid var(--brand)", borderRadius: 12 }}>🧭 Tester la visite</button>
      </div>
    </div>
  );
}

function RevenueManager() {
  const { data: st } = useQuery({ queryKey: ["admin-stats"], queryFn: adminStats });
  if (!st) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;
  const m = st.members, r = st.revenue, p = st.prices;
  const eur = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  const Stat = ({ label, value, sub, accent, testid }) => (
    <div style={{ padding: "16px 18px", borderRadius: 14, border: "1px solid var(--border-c)", background: accent ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "var(--bg-primary)" }} data-testid={testid}>
      <div style={{ fontSize: 26, fontWeight: 900, color: accent ? "var(--brand)" : "var(--text-primary)", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 2, opacity: 0.8 }}>{sub}</div>}
    </div>
  );
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 };
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-secondary)" }}>Membres</p>
        <div style={grid}>
          <Stat testid="stat-total" label="Membres au total" value={m.total} />
          <Stat testid="stat-subs" label="Abonnés actifs" value={m.subscribers} accent />
          <Stat testid="stat-lifetime" label="Accès à vie" value={m.lifetime} />
          <Stat testid="stat-coaching" label="En accompagnement" value={m.coaching} sub="Renzoku à réserver" />
          <Stat testid="stat-new" label="Nouveaux (30 j)" value={m.new_30d} />
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-secondary)" }}>Revenus</p>
        <div style={grid}>
          <Stat testid="rev-mrr" label="Revenu mensuel récurrent" value={eur(r.mrr)} sub={`${m.subscribers} × ${eur(p.monthly_eur)}`} accent />
          <Stat testid="rev-annual" label="Projection annuelle" value={eur(r.annual_projection)} sub="MRR × 12" />
          <Stat testid="rev-total" label="Total encaissé" value={eur(r.collected_total)} sub="Depuis le début (Stripe)" />
          <Stat testid="rev-30d" label="Encaissé (30 j)" value={eur(r.collected_30d)} />
          <Stat testid="rev-coaching" label="Encaissé accompagnement" value={eur(r.collected_coaching)} sub={`Renzoku ${eur(p.renzoku_eur)}`} />
          <Stat testid="rev-subs" label="Encaissé abonnements" value={eur(r.collected_subscriptions)} />
        </div>
      </div>
      <p className="text-[11.5px]" style={{ color: "var(--text-secondary)" }}>💡 Le « revenu mensuel récurrent » est estimé (abonnés actifs × prix mensuel). L'« encaissé » provient des paiements Stripe réellement réglés. Les accès à vie et offerts ne comptent pas dans le récurrent.</p>
    </div>
  );
}

function AuthBannersManager() {
  const { data: settings, refetch } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!settings) return <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Chargement…</p>;
  const newTxt = draft?.newT ?? (settings.auth_banner_new || "");
  const lifeTxt = draft?.lifeT ?? (settings.auth_banner_lifetime || "");
  const retTxt = draft?.retT ?? (settings.auth_banner_return || "");
  const save = async () => {
    setBusy(true);
    try {
      await updateSettings({ ...settings, auth_banner_new: newTxt, auth_banner_lifetime: lifeTxt, auth_banner_return: retTxt });
      toast.success("Bannières mises à jour"); setDraft(null); refetch();
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const iSt = { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-primary)", boxSizing: "border-box", fontFamily: "inherit", minHeight: 64, resize: "vertical" };
  const lbl = { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text-secondary)" };
  return (
    <div className="space-y-4">
      <div>
        <label style={lbl}>Bannière — accès offert « membre » (crée son mot de passe)</label>
        <textarea style={iSt} value={newTxt} onChange={(e) => setDraft({ newT: e.target.value, lifeT: lifeTxt, retT: retTxt })} data-testid="banner-new-input" />
      </div>
      <div>
        <label style={lbl}>Bannière — accès « à vie » (crée son mot de passe)</label>
        <textarea style={iSt} value={lifeTxt} onChange={(e) => setDraft({ newT: newTxt, lifeT: e.target.value, retT: retTxt })} data-testid="banner-lifetime-input" />
      </div>
      <div>
        <label style={lbl}>Bannière — membre existant (entre son mot de passe)</label>
        <textarea style={iSt} value={retTxt} onChange={(e) => setDraft({ newT: newTxt, lifeT: lifeTxt, retT: e.target.value })} data-testid="banner-return-input" />
      </div>
      <button onClick={save} disabled={busy} data-testid="banner-save" className="px-5 py-2.5 font-bold" style={{ background: "var(--brand)", color: "#fff", borderRadius: 12, opacity: busy ? 0.6 : 1 }}>{busy ? "Enregistrement…" : "Enregistrer les bannières"}</button>
    </div>
  );
}

function SubscriberSpace({ user }) {
  const [busy, setBusy] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const at = user?.access_type;
  const isLifetime = at === "lifetime" || user?.role === "admin";
  const isSubscription = at === "subscription";
  const statusLabel = user?.role === "admin" ? "Créateur · Admin"
    : isLifetime ? "Accès à vie"
    : isSubscription ? (user?.subscription_status === "canceled" ? "Abonnement résilié" : "Abonné actif")
    : "Membre";
  const manage = async () => {
    setBusy(true);
    try { const { url } = await openBillingPortal(); window.location.href = url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Aucun abonnement Stripe à gérer sur ce compte."); setBusy(false); }
  };
  const deleteAccount = async () => {
    if (!window.confirm("Supprimer définitivement ton compte ? Cette action est irréversible : ton carnet, tes exercices et tes données seront effacés.")) return;
    if (!window.confirm("Es-tu vraiment sûr·e ? Si tu as un abonnement Stripe actif, pense à le résilier d'abord via « Gérer mon abonnement ».")) return;
    setDelBusy(true);
    try { await deleteMyAccount(); toast.success("Compte supprimé. À bientôt."); await logout(); navigate("/", { replace: true }); }
    catch { toast.error("Suppression impossible. Réessaie."); setDelBusy(false); }
  };
  return (
    <>
      <PremiumCard title="Mon Carnet" sub="Ton espace personnel : exercices, notes et brouillons classés par Porte." kanji="帳" testid="card-carnet">
        <button onClick={() => navigate("/carnet")} data-testid="open-my-carnet" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 10, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>Ouvrir mon carnet →</button>
      </PremiumCard>
      <PremiumCard title="Mon abonnement" sub="Gère ton abonnement, ta carte bancaire et tes factures en toute autonomie." kanji="会" testid="card-subscription">
        <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="sub-status">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold" style={{ borderRadius: 999, background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>{statusLabel}</span>
          {typeof user?.coaching_credits === "number" && user.coaching_credits > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold" style={{ borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-primary)", border: "1px solid var(--border-c)" }}>{user.coaching_credits} crédit{user.coaching_credits > 1 ? "s" : ""} coaching</span>
          )}
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
          {isLifetime
            ? "Tu bénéficies d'un accès à vie 🎉 Tu peux tout de même gérer ta carte / tes factures, résilier un éventuel abonnement, ou supprimer ton compte à tout moment."
            : "Mets à jour ta carte bancaire, télécharge tes factures ou résilie ton abonnement à tout moment via le portail sécurisé Stripe. La résiliation prend effet à la fin de la période en cours."}
        </p>
        <button data-testid="sub-manage-btn" onClick={manage} disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-3 font-bold transition-all hover:-translate-y-0.5"
          style={{ borderRadius: 12, border: "none", background: "var(--brand)", color: "#fff", fontSize: 14.5, opacity: busy ? 0.6 : 1, boxShadow: "0 6px 18px color-mix(in srgb, var(--brand) 40%, transparent)" }}>
          {busy ? "Ouverture…" : "Gérer / résilier mon abonnement"}
        </button>
      </PremiumCard>
      <PremiumCard title="Zone sensible" sub="Supprimer définitivement ton compte et toutes tes données du Dōjō." kanji="削" testid="card-danger">
        <button data-testid="delete-account-btn" onClick={deleteAccount} disabled={delBusy}
          className="inline-flex items-center gap-2 px-5 py-3 font-bold transition-all"
          style={{ borderRadius: 12, border: "1.5px solid var(--brand)", background: "transparent", color: "var(--brand)", fontSize: 14, opacity: delBusy ? 0.6 : 1 }}>
          {delBusy ? "Suppression…" : "Supprimer mon compte"}
        </button>
      </PremiumCard>
    </>
  );
}


export default function Settings() {
  const nav = useNavigate();
  const { theme, setTheme, isAdmin, musicVolume, setMusicVolume, muted, toggleMute, sfx,
    accent, setAccent, radius, setRadius, reduceMotion, setReduceMotion, cardTagline, setCardTagline,
    posterDesktop, posterMobile, setPosterSizeFor, textDesktop, textMobile, setTextScaleFor } = useApp();
  const { user, logout } = useAuth();
  const [notif, setNotif] = useState(() => localStorage.getItem("td_notif") !== "0");
  const [tab, setTab] = useState("abonnement");
  const [nerodusHidden, setNerodusHidden] = useState(() => localStorage.getItem("nerodus_hidden") === "1");
  const [nerodusAutoTips, setNerodusAutoTips] = useState(() => localStorage.getItem("nerodus_autotips_off") !== "1");
  const applyNerodus = (hidden, autoTips) => {
    localStorage.setItem("nerodus_hidden", hidden ? "1" : "0");
    localStorage.setItem("nerodus_autotips_off", autoTips ? "0" : "1");
    window.dispatchEvent(new CustomEvent("nerodus-prefs"));
  };
  const replayNerodus = () => {
    Object.keys(localStorage).filter((k) => k.startsWith("nerodus_seen_")).forEach((k) => localStorage.removeItem(k));
    window.dispatchEvent(new CustomEvent("nerodus-prefs", { detail: { replay: true } }));
    toast.success("Nerodus va te réexpliquer les pages.");
  };
  const { data: siteSettings, refetch: refetchSite } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const defTheme = siteSettings?.default_theme || "tengoku";
  const setDefaultTheme = async (k) => { await updateSettings({ ...siteSettings, default_theme: k }); toast.success("Thème par défaut défini"); refetchSite(); };

  const ACCENTS = [
    { v: "", label: "Thème", c: "var(--brand)" }, { v: "#e60012", label: "Rouge", c: "#e60012" },
    { v: "#ff7a00", label: "Orange", c: "#ff7a00" }, { v: "#ffd400", label: "Or", c: "#ffd400" },
    { v: "#00e5a0", label: "Jade", c: "#00e5a0" }, { v: "#00fff5", label: "Cyan", c: "#00fff5" },
    { v: "#4d8dff", label: "Bleu", c: "#4d8dff" }, { v: "#c4b5fd", label: "Lilas", c: "#c4b5fd" },
    { v: "#ff4dcb", label: "Rose", c: "#ff4dcb" }, { v: "#ffffff", label: "Blanc", c: "#ffffff" },
  ];
  const RADII = [{ v: "", label: "Thème" }, { v: "0px", label: "Net" }, { v: "10px", label: "Arrondi" }, { v: "18px", label: "Doux" }, { v: "999px", label: "Pilule" }];
  const POSTERS = [{ v: "compact", label: "Compact" }, { v: "normal", label: "Normal" }, { v: "grand", label: "Grand" }];
  const TEXT_SCALES = [
    { v: "petit", label: "Petit" }, { v: "normal", label: "Normal" }, { v: "grand", label: "Grand" },
    { v: "xl", label: "Très grand" }, { v: "max", label: "Maximum" },
  ];
  const TEXT_SCALE_SIZE = { petit: 13, normal: 16, grand: 19, xl: 23, max: 27 };

  const TABS = [
    { key: "abonnement", label: "Espace Abonné", kanji: "会" },
    { key: "apparence", label: "Apparence", kanji: "色" },
    { key: "confort", label: "Confort", kanji: "楽" },
    { key: "son", label: "Son", kanji: "音" },
    { key: "compte", label: "Mon compte", kanji: "人" },
    ...(isAdmin ? [{ key: "createur", label: "Espace Créateur", kanji: "⛩", accent: true }] : []),
  ];

  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const roleLabel = user?.role === "admin" ? "Créateur · Admin" : (user?.is_subscriber ? "Abonné" : "Membre");

  const Segmented = ({ options, current, onSelect, testid, render }) => (
    <div className="inline-flex flex-wrap gap-1.5 p-1.5" data-testid={testid}
      style={{ borderRadius: 999, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)" }}>
      {options.map((o) => {
        const active = current === o.v;
        return (
          <button key={o.v} data-testid={o.testid} onClick={() => { onSelect(o.v); sfx("nav"); }}
            className="px-3.5 py-2 transition-all"
            style={{ borderRadius: 999, fontSize: 13, fontWeight: 700,
              background: active ? "var(--brand)" : "transparent",
              color: active ? "#fff" : "var(--text-secondary)",
              boxShadow: active ? "0 4px 14px color-mix(in srgb, var(--brand) 35%, transparent)" : "none" }}>
            {render ? render(o, active) : o.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="relative min-h-screen w-full" style={{ background: "var(--bg-primary)" }}>
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ borderBottom: "1px solid var(--border-c)" }}>
        <span aria-hidden style={{ position: "absolute", right: "-2vw", top: "-6vh", fontSize: "34vh", fontWeight: 800, color: "var(--text-primary)", opacity: 0.04, pointerEvents: "none", lineHeight: 1, fontFamily: "var(--font-display)" }}>道場</span>
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-7 pb-8">
          <button data-testid="settings-back" onClick={() => { sfx("back"); nav(-1); }}
            className="flex items-center gap-2 px-3.5 py-2 mb-7 transition-all hover:gap-3"
            style={{ borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}>
            <IoArrowBack /> <span className="text-sm font-semibold">Retour</span>
          </button>
          <div className="flex items-center gap-4">
            {user?.avatar
              ? <img src={user.avatar} alt="" style={{ width: 68, height: 68, borderRadius: "50%", objectFit: "cover", border: "2.5px solid var(--brand)", flexShrink: 0 }} />
              : <span style={{ width: 68, height: 68, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: 30, flexShrink: 0 }}>{initial}</span>}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: "var(--brand)" }}>Paramètres</p>
              <h1 className="font-display text-3xl md:text-4xl leading-none truncate" style={{ color: "var(--text-primary)" }}>{user?.name || "Ton Dōjō"}</h1>
              <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 text-xs font-bold" style={{ borderRadius: 999, background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>{roleLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-7 md:flex md:gap-8">
        {/* Tab rail */}
        <nav data-testid="settings-tabs" className="md:w-52 md:flex-shrink-0 mb-6 md:mb-0">
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-1 md:sticky md:top-6">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button key={t.key} data-testid={`settings-tab-${t.key}`} onClick={() => { setTab(t.key); sfx("select"); }}
                  className="flex items-center gap-3 px-4 py-3 whitespace-nowrap transition-all flex-shrink-0"
                  style={{ borderRadius: 14, fontWeight: 700, fontSize: 14.5, textAlign: "left",
                    background: active ? (t.accent ? "var(--brand)" : "var(--bg-secondary)") : "transparent",
                    color: active ? (t.accent ? "#fff" : "var(--text-primary)") : "var(--text-secondary)",
                    border: active && !t.accent ? "1px solid var(--border-c)" : "1px solid transparent",
                    boxShadow: active ? "var(--surface-shadow)" : "none" }}>
                  <span className="font-display text-lg" style={{ width: 22, textAlign: "center", color: active && !t.accent ? "var(--brand)" : "inherit" }}>{t.kanji}</span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0" data-testid={`settings-panel-${tab}`}>
          {tab === "abonnement" && (
            <SubscriberSpace user={user} />
          )}
          {tab === "apparence" && (
            <>
              <PremiumCard title="Thème du Dōjō" sub="Change l'ambiance visuelle de toute la plateforme." kanji="色" testid="card-themes">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="theme-grid">
                  {THEMES.filter((t) => FEATURED_THEME_KEYS.includes(t.key)).map((t) => {
                    const active = theme === t.key;
                    const p = THEME_PREVIEWS[t.key];
                    return (
                      <button key={t.key} data-testid={`theme-${t.key}`} onClick={() => { setTheme(t.key); sfx("select"); toast.success(`Thème ${t.name}`); }}
                        className="relative text-left overflow-hidden transition-all hover:-translate-y-1"
                        style={{ borderRadius: 18, border: active ? "2px solid var(--brand)" : "1px solid var(--border-c)", background: "var(--bg-secondary)" }}>
                        {/* mini mockup */}
                        <div style={{ height: 78, background: p.bg, padding: 10, display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ width: 16, height: 16, borderRadius: 4, background: p.brand }} />
                            <span style={{ flex: 1, height: 7, borderRadius: 4, background: p.text, opacity: 0.85 }} />
                          </div>
                          <div style={{ height: 20, borderRadius: 6, background: p.card }} />
                          <div style={{ display: "flex", gap: 5 }}>
                            <span style={{ width: 34, height: 12, borderRadius: 6, background: p.brand }} />
                            <span style={{ flex: 1, height: 12, borderRadius: 6, background: p.card }} />
                          </div>
                        </div>
                        <div className="p-3.5">
                          <div className="flex items-baseline justify-between">
                            <span className="font-display text-lg" style={{ color: "var(--text-primary)" }}>{t.name}</span>
                            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t.kanji}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {active && <span className="flex items-center gap-1 text-xs font-bold" style={{ color: "var(--brand)" }}><IoCheckmarkCircle /> Actif</span>}
                            {isAdmin && (
                              <span role="button" data-testid={`theme-default-${t.key}`} title="Définir par défaut"
                                onClick={(e) => { e.stopPropagation(); setDefaultTheme(t.key); }}
                                className="ml-auto inline-flex items-center gap-1 text-[11px] cursor-pointer" style={{ color: defTheme === t.key ? "var(--brand)" : "var(--text-secondary)" }}>
                                {defTheme === t.key ? <IoStar size={13} /> : <IoStarOutline size={13} />} défaut
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </PremiumCard>

              {isAdmin && (
                <PremiumCard title="Habillage avancé" sub="Options réservées au créateur : accent, coins et affiches." kanji="✎" testid="card-advanced">
                  <p className="text-xs uppercase tracking-widest mb-2.5" style={{ color: "var(--text-secondary)" }}>Couleur d'accent</p>
                  <div className="flex flex-wrap gap-2 mb-6" data-testid="accent-grid">
                    {ACCENTS.map((a) => (
                      <button key={a.label} data-testid={`accent-${a.v || "theme"}`} onClick={() => { setAccent(a.v); sfx("select"); }}
                        title={a.label} className="w-10 h-10 flex items-center justify-center transition-transform hover:scale-110"
                        style={{ background: a.c, borderRadius: 12, border: "1px solid var(--border-c)", outline: accent === a.v ? "2px solid var(--text-primary)" : "none", outlineOffset: 2 }}>
                        {accent === a.v && <IoCheckmarkCircle style={{ color: "#000", mixBlendMode: "difference" }} />}
                      </button>
                    ))}
                    <label className="w-10 h-10 flex items-center justify-center cursor-pointer text-sm" style={{ background: "var(--bg-tertiary)", borderRadius: 12, border: "1px solid var(--border-c)" }} title="Couleur personnalisée" data-testid="accent-custom">
                      🎨<input type="color" hidden value={accent || "#e60012"} onChange={(e) => setAccent(e.target.value)} />
                    </label>
                  </div>

                  <p className="text-xs uppercase tracking-widest mb-2.5" style={{ color: "var(--text-secondary)" }}>Style des coins</p>
                  <div className="mb-6">
                    <Segmented testid="radius-grid" current={radius} onSelect={setRadius}
                      options={RADII.map((r) => ({ v: r.v, label: r.label, testid: `radius-${r.v || "theme"}` }))} />
                  </div>

                  <p className="text-xs uppercase tracking-widest mb-2.5" style={{ color: "var(--text-secondary)" }}>Taille des affiches</p>
                  {[{ phone: false, label: "Ordinateur", cur: posterDesktop }, { phone: true, label: "Mobile", cur: posterMobile }].map((dev) => (
                    <div key={dev.label} className="mb-4 flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-widest mb-1.5" style={{ color: "var(--brand)" }}>{dev.label}</p>
                        <Segmented testid={`poster-size-${dev.phone ? "mobile" : "desktop"}-grid`} current={dev.cur || (dev.phone ? "compact" : "normal")} onSelect={(v) => setPosterSizeFor(dev.phone, v)}
                          options={POSTERS.map((p) => ({ v: p.v, label: p.label, testid: `poster-${dev.phone ? "mobile" : "desktop"}-${p.v}` }))} />
                      </div>
                      <MiniPoster size={dev.cur || (dev.phone ? "compact" : "normal")} />
                    </div>
                  ))}
                </PremiumCard>
              )}
            </>
          )}

          {tab === "confort" && (
            <>
              <PremiumCard title="Taille du texte" sub="Agrandit tout le texte de l'application. Réglable séparément pour l'ordinateur et le mobile." kanji="楽" testid="card-textsize">
                {[{ phone: false, label: "Ordinateur", cur: textDesktop }, { phone: true, label: "Mobile", cur: textMobile }].map((dev) => (
                  <div key={dev.label} className="mb-5">
                    <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--brand)" }}>{dev.label}</p>
                    <div className="flex flex-wrap items-stretch gap-2.5" data-testid={`text-scale-${dev.phone ? "mobile" : "desktop"}-grid`}>
                      {TEXT_SCALES.map((t) => {
                        const active = (dev.cur || "grand") === t.v;
                        return (
                          <button key={t.v} data-testid={`text-${dev.phone ? "mobile" : "desktop"}-${t.v}`} onClick={() => { setTextScaleFor(dev.phone, t.v); sfx("nav"); }}
                            className="flex-1 min-w-[70px] flex flex-col items-center justify-center gap-1.5 py-3 px-2 transition-all hover:-translate-y-0.5"
                            style={{ background: "var(--bg-secondary)", borderRadius: 14, border: active ? "2px solid var(--brand)" : "1px solid var(--border-c)" }}>
                            <span className="font-display leading-none" style={{ fontSize: TEXT_SCALE_SIZE[t.v], color: active ? "var(--brand)" : "var(--text-primary)" }}>A</span>
                            <span className="text-xs font-semibold" style={{ color: active ? "var(--brand)" : "var(--text-secondary)" }}>{t.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </PremiumCard>

              <PremiumCard title="Confort & animations" kanji="静" testid="card-comfort">
                <div className="space-y-3">
                  <ToggleRow testid="toggle-motion" label="Réduire les animations" hint="Confort visuel / économie de batterie" checked={reduceMotion} onChange={setReduceMotion} />
                  <ToggleRow testid="toggle-card-tagline" label="Afficher les accroches sur les cartes" hint="Affiche un court texte sous chaque affiche" checked={cardTagline} onChange={setCardTagline} />
                </div>
              </PremiumCard>

              <PremiumCard title="Nerodus — ton guide" sub="Nerodus est le petit renard qui te donne des conseils sur chaque page." kanji="狐" testid="card-nerodus-prefs">
                <div className="space-y-3">
                  <ToggleRow testid="toggle-nerodus-show" label="Afficher Nerodus" hint="Masque complètement la bulle de Nerodus" checked={!nerodusHidden} onChange={(v) => { setNerodusHidden(!v); applyNerodus(!v, nerodusAutoTips); }} />
                  <ToggleRow testid="toggle-nerodus-autotips" label="Conseils automatiques" hint="Nerodus s'ouvre tout seul la 1re fois que tu découvres une page" checked={nerodusAutoTips} onChange={(v) => { setNerodusAutoTips(v); applyNerodus(nerodusHidden, v); }} />
                  <button data-testid="btn-nerodus-replay" onClick={replayNerodus}
                    className="w-full p-3 text-sm font-semibold transition-all"
                    style={{ borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", cursor: "pointer" }}>
                    ↺ Revoir les conseils depuis le début
                  </button>
                </div>
              </PremiumCard>
            </>
          )}

          {tab === "son" && (
            <>
            <PremiumCard title="Ambiance sonore" sub="Contrôle la musique d'ambiance et les effets de l'interface." kanji="音" testid="card-sound">
              <ToggleRow testid="toggle-mute" label={muted ? "Son coupé" : "Son activé"} hint={muted ? "Aucun son ne sera joué" : "Musique et effets audibles"} checked={!muted} onChange={() => toggleMute()} />
              <div className="mt-5 p-4" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", opacity: muted ? 0.5 : 1, pointerEvents: muted ? "none" : "auto" }}>
                <VolumeSlider value={musicVolume} onChange={setMusicVolume} />
              </div>
            </PremiumCard>
            <PremiumCard title="Musique d'ambiance" sub="Choisis la musique de fond du Dōjō. Ton choix et ton volume sont mémorisés." kanji="♪" testid="card-ambience-son"><AmbienceManager /></PremiumCard>
            </>
          )}

          {tab === "compte" && (
            <>
              <PremiumCard title="Mon compte" kanji="人" testid="card-account">
                <div className="p-4 mb-4" style={{ borderRadius: 14, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)" }}>
                  <p className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>Connecté en tant que</p>
                  <p className="text-sm mt-1 font-semibold break-all" style={{ color: "var(--text-primary)" }} data-testid="account-email">{user?.email || "—"}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button data-testid="account-profile" onClick={() => nav("/profil")} className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}>✎ Modifier mon profil</button>
                  <a data-testid="account-discord" href={DISCORD_INVITE} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", textDecoration: "none" }}>💬 Rejoindre le Discord</a>
                  <a data-testid="account-support" href={DISCORD_INVITE} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", textDecoration: "none" }}>🛟 Contacter le support</a>
                  <button data-testid="account-coaching" onClick={() => nav("/accompagnement")} className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--brand)", background: "color-mix(in srgb, var(--brand) 8%, transparent)", color: "var(--brand)" }}>⛩ Coaching Renzoku</button>
                  <button data-testid="account-tour" onClick={() => { try { sessionStorage.setItem("sx_intro_shown", "1"); } catch (e) {} window.dispatchEvent(new Event("td-start-tour")); }} className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}>🧭 Refaire la visite guidée</button>
                  <button data-testid="sub-manage-btn" onClick={async () => { try { const { url } = await openBillingPortal(); window.location.href = url; } catch (e) { toast.error(e?.response?.data?.detail || "Aucun abonnement Stripe à gérer sur ce compte."); } }} className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-left transition-all hover:-translate-y-0.5" style={{ borderRadius: 14, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)" }}>💳 Gérer / résilier mon abonnement</button>
                </div>
                <div className="mt-3">
                  <ToggleRow testid="account-notif" label="🔔 Notifications" hint="Sois prévenu des nouveautés du Dōjō" checked={notif}
                    onChange={(v) => { setNotif(v); localStorage.setItem("td_notif", v ? "1" : "0"); toast.success(v ? "Notifications activées" : "Notifications désactivées"); }} />
                </div>
                <button data-testid="account-logout" onClick={async () => { await logout(); nav("/connexion"); }} className="mt-5 w-full px-4 py-3.5 text-sm font-bold transition-all hover:opacity-90" style={{ background: "var(--brand)", color: "#fff", borderRadius: 14 }}>Se déconnecter</button>
                <button data-testid="delete-account-btn" onClick={async () => {
                  if (!window.confirm("Supprimer définitivement ton compte ? Cette action est irréversible : ton carnet, tes exercices et tes données seront effacés.")) return;
                  if (!window.confirm("Es-tu vraiment sûr·e ? Si tu as un abonnement Stripe actif, pense à le résilier d'abord.")) return;
                  try { await deleteMyAccount(); toast.success("Compte supprimé. À bientôt."); await logout(); nav("/", { replace: true }); } catch { toast.error("Suppression impossible. Réessaie."); }
                }} className="mt-3 w-full px-4 py-3 text-sm font-bold transition-all" style={{ background: "transparent", color: "var(--brand)", border: "1.5px solid var(--brand)", borderRadius: 14 }}>Supprimer mon compte</button>
              </PremiumCard>
              <p className="text-xs text-center" style={{ color: "var(--text-secondary)" }}>Astuce : installe Tengoku Dōjō via « Ajouter à l'écran d'accueil ».</p>
            </>
          )}

          {tab === "createur" && isAdmin && (
            <>
              <div className="mb-6 flex items-center gap-3">
                <span className="font-display text-3xl" style={{ color: "var(--brand)" }}>⛩</span>
                <div>
                  <h2 className="font-display text-2xl leading-none" style={{ color: "var(--text-primary)" }}>Espace Créateur</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Ces réglages ne sont visibles que par toi (administrateur).</p>
                </div>
              </div>
              <PremiumCard title="Membres & Accès" sub="Gère les abonnés, offre des accès (anciens membres / à vie), suspends ou supprime des comptes." kanji="人" testid="card-members-admin">
                <button onClick={() => nav("/admin/membres")} data-testid="open-admin-members" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 10, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>Ouvrir la console membres →</button>
              </PremiumCard>
              <PremiumCard title="Revenus & Évolution" sub="Suis en un coup d'œil tes membres, tes abonnés, l'accompagnement et tes revenus." kanji="円" testid="card-revenue"><RevenueManager /></PremiumCard>
              <PremiumCard title="Questionnaire d'arrivée" sub="Les questions que Nerodus pose aux nouveaux membres. Leurs réponses apparaissent sur leur profil." kanji="問" testid="card-questionnaire"><QuestionnaireManager /></PremiumCard>
              <PremiumCard title="Offre Renzoku" sub="Réglages du coaching : séance offerte à l'abonnement, lien Calendly, et don manuel via la console membres." kanji="導" testid="card-renzoku"><RenzokuOfferManager /></PremiumCard>
              <PremiumCard title="Visite guidée" sub="La visite que Nerodus propose après le questionnaire. Modifie les étapes et les textes, puis teste-la." kanji="🧭" testid="card-tour"><TourManager /></PremiumCard>
              <PremiumCard title="Bannières de connexion" sub="Les trois messages affichés dans l'onglet Connexion : accès offert « membre », accès « à vie », et retour d'un membre existant." kanji="✉" testid="card-auth-banners"><AuthBannersManager /></PremiumCard>
              <PremiumCard title="Icône de l'application" sub="Change le logo/favicon affiché dans l'onglet et sur l'écran d'accueil." kanji="✦" testid="card-branding"><BrandingManager /></PremiumCard>
              <PremiumCard title="Nerodus — le guide" sub="Personnalise ton bibliothécaire et ce qu'il dit sur chaque page." testid="card-nerodus"><NerodusManager /></PremiumCard>
              <PremiumCard title="Écran de démarrage" sub="Intro cinématique type PS2 / Steam." testid="card-intro"><IntroManager /></PremiumCard>
              <PremiumCard title="Annonces aux visiteurs" sub="Préviens d'une nouveauté." testid="card-announcements"><AnnouncementsManager /></PremiumCard>
              <PremiumCard title="Messages de bienvenue" sub="Affichés sur l'écran d'entrée." testid="card-welcome"><WelcomeMessagesManager /></PremiumCard>
              <PremiumCard title="Catégories" testid="card-categories"><CategoryManager /></PremiumCard>
              <PremiumCard title="Abonnés newsletter" testid="card-subscribers"><SubscribersManager /></PremiumCard>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
